/* BLEEN — Interazioni correttive CASABLEEN
   Caricato una sola volta dopo bleen-v2.js.
   INT-01 ricerca predittiva | INT-02 filtri automatici | BUG-03 disponibilità */
(() => {
  'use strict';

  if (window.__bleenFixesLoaded) return;
  window.__bleenFixesLoaded = true;

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#039;', '"': '&quot;'
  })[character]);

  const initFilters = (scope = document) => {
    scope.querySelectorAll('[data-collection-filters]').forEach((filterForm) => {
      if (filterForm.dataset.bleenFiltersReady === 'true') return;
      filterForm.dataset.bleenFiltersReady = 'true';
      let submitting = false;

      const applyFilters = () => {
        if (submitting) return;
        submitting = true;
        filterForm.setAttribute('aria-busy', 'true');
        filterForm.dataset.bleenApplying = 'true';
        if (typeof filterForm.requestSubmit === 'function') filterForm.requestSubmit();
        else filterForm.submit();
      };

      filterForm.addEventListener('change', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;
        if (target.matches('input[type="checkbox"], input[type="number"]')) applyFilters();
      });

      filterForm.addEventListener('keydown', (event) => {
        const target = event.target;
        if (event.key !== 'Enter' || !(target instanceof HTMLInputElement) || !target.matches('input[type="number"]')) return;
        event.preventDefault();
        applyFilters();
      });
    });
  };

  const syncAvailability = (scope = document) => {
    scope.querySelectorAll('.bleen-product-info').forEach((info) => {
      const submit = info.querySelector('[data-product-submit]');
      const availability = info.querySelector('[data-product-availability]');
      if (!submit || !availability) return;
      availability.dataset.availabilityState = submit.disabled ? 'unavailable' : 'available';
    });
  };

  const initPredictiveSearch = (scope = document) => {
    scope.querySelectorAll('input[type="search"][name="q"]').forEach((input, inputIndex) => {
      if (input.dataset.bleenPredictiveReady === 'true') return;
      const form = input.closest('form');
      if (!form) return;

      input.dataset.bleenPredictiveReady = 'true';
      const panel = document.createElement('div');
      const panelId = `BleenPredictive-${Date.now()}-${inputIndex}`;
      panel.id = panelId;
      panel.className = 'bleen-predictive';
      panel.setAttribute('role', 'listbox');
      panel.hidden = true;
      form.style.position = form.style.position || 'relative';
      form.appendChild(panel);

      input.setAttribute('aria-controls', panelId);
      input.setAttribute('aria-expanded', 'false');
      input.setAttribute('aria-autocomplete', 'list');
      input.setAttribute('aria-haspopup', 'listbox');

      let controller = null;
      let timer = null;
      let activeIndex = -1;

      const items = () => [...panel.querySelectorAll('.bleen-predictive__item')];

      const close = () => {
        panel.hidden = true;
        panel.innerHTML = '';
        activeIndex = -1;
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
        input.removeAttribute('aria-busy');
      };

      const focusItem = (index) => {
        const options = items();
        if (!options.length) return;
        activeIndex = Math.max(-1, Math.min(index, options.length - 1));
        options.forEach((option, optionIndex) => option.classList.toggle('is-active', optionIndex === activeIndex));
        if (activeIndex === -1) {
          input.focus();
          input.removeAttribute('aria-activedescendant');
          return;
        }
        const option = options[activeIndex];
        option.focus();
        input.setAttribute('aria-activedescendant', option.id);
      };

      const render = (products, collections, term) => {
        const limitedProducts = products.slice(0, 5);
        const limitedCollections = collections.slice(0, Math.max(0, 6 - limitedProducts.length));
        const rootUrl = window.Shopify?.routes?.root || '/';

        if (!limitedProducts.length && !limitedCollections.length) {
          panel.innerHTML = '<p class="bleen-predictive__empty">Nessun risultato. Prova con un altro termine.</p>';
          panel.hidden = false;
          input.setAttribute('aria-expanded', 'true');
          input.removeAttribute('aria-busy');
          return;
        }

        const productMarkup = limitedProducts.map((product, index) => {
          const image = product.featured_image?.url
            ? `<img src="${escapeHtml(product.featured_image.url)}" alt="" width="48" height="48" loading="lazy">`
            : '<span class="bleen-predictive__placeholder" aria-hidden="true"></span>';
          const price = product.price ? `<span class="bleen-predictive__price">${escapeHtml(product.price)}</span>` : '';
          return `<a id="${panelId}-product-${index}" class="bleen-predictive__item" role="option" href="${escapeHtml(product.url)}">${image}<span class="bleen-predictive__text"><strong>${escapeHtml(product.title)}</strong>${price}</span></a>`;
        }).join('');

        const collectionMarkup = limitedCollections.map((collection, index) => (
          `<a id="${panelId}-collection-${index}" class="bleen-predictive__item bleen-predictive__item--collection" role="option" href="${escapeHtml(collection.url)}"><span class="bleen-predictive__text"><strong>${escapeHtml(collection.title)}</strong><span class="bleen-predictive__meta">Collezione</span></span></a>`
        )).join('');

        const allResultsUrl = `${rootUrl}search?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`;
        panel.innerHTML = `${productMarkup}${collectionMarkup}<a class="bleen-predictive__all" href="${escapeHtml(allResultsUrl)}">Vedi tutti i risultati</a>`;
        panel.hidden = false;
        activeIndex = -1;
        input.setAttribute('aria-expanded', 'true');
        input.removeAttribute('aria-busy');
      };

      const search = async (term) => {
        controller?.abort();
        controller = new AbortController();
        const rootUrl = window.Shopify?.routes?.root || '/';
        const url = `${rootUrl}search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product,collection&resources[limit]=6&resources[options][unavailable_products]=last`;

        input.setAttribute('aria-busy', 'true');
        panel.innerHTML = '<p class="bleen-predictive__empty">Ricerca in corso…</p>';
        panel.hidden = false;
        input.setAttribute('aria-expanded', 'true');

        try {
          const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('predictive-search');
          const data = await response.json();
          const results = data?.resources?.results || {};
          render(results.products || [], results.collections || [], term);
        } catch (error) {
          if (error.name !== 'AbortError') close();
        }
      };

      input.addEventListener('input', () => {
        const term = input.value.trim();
        window.clearTimeout(timer);
        if (term.length < 2) {
          controller?.abort();
          close();
          return;
        }
        timer = window.setTimeout(() => search(term), 250);
      });

      form.addEventListener('keydown', (event) => {
        if (panel.hidden) return;
        const options = items();

        if (event.key === 'Escape') {
          event.preventDefault();
          close();
          input.focus();
          return;
        }

        if (!options.length || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'ArrowDown') focusItem(activeIndex + 1);
        if (event.key === 'ArrowUp') focusItem(activeIndex - 1);
        if (event.key === 'Home') focusItem(0);
        if (event.key === 'End') focusItem(options.length - 1);
      });

      document.addEventListener('click', (event) => {
        if (!form.contains(event.target)) close();
      });

      form.addEventListener('focusout', () => {
        window.setTimeout(() => {
          if (!form.contains(document.activeElement)) close();
        }, 0);
      });
    });
  };

  const initAll = (scope = document) => {
    initFilters(scope);
    syncAvailability(scope);
    initPredictiveSearch(scope);
  };

  initAll();
  document.addEventListener('change', (event) => {
    if (event.target.closest('[data-product-variant-select]')) window.setTimeout(() => syncAvailability(), 0);
  });
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
